/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { Region } from '@/components/region';
import ImageGallery from '@/components/image-gallery';
import ImageGalleryThumbs from '@/components/image-gallery-thumbs';
import type { GalleryImage } from '@/components/image-gallery/types';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

interface ProductBundleMediaProps {
    /** Page Designer page promise carrying the `bundleMarketingContent` region (CMS headline/subhead/description). */
    page: Promise<PageWithComponentData | null> | PageWithComponentData | null;
    images: GalleryImage[];
    productName?: string;
    badge?: ReactNode;
}

/**
 * Two-column media block for the Product Bundle Page: CMS marketing copy on the left, main gallery image on
 * the right, with a full-width thumbnail row below both. `ImageGallery` and `ImageGalleryThumbs` share
 * controlled selection state here since the spec calls for the thumbnail row to run the full width of the
 * media block rather than sit under the gallery column alone.
 */
export default function ProductBundleMedia({
    page,
    images,
    productName,
    badge,
}: ProductBundleMediaProps): ReactElement {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
                <div className="order-2 lg:order-1">
                    <Region
                        page={page}
                        regionId="bundleMarketingContent"
                        errorElement={
                            <div
                                className="bg-muted rounded-none aspect-square overflow-hidden flex items-center justify-center"
                                aria-hidden="true"
                            />
                        }
                    />
                </div>
                <div className="order-1 lg:order-2">
                    <ImageGallery
                        images={images}
                        eager
                        hideThumbnails
                        selectedImageIndex={selectedImageIndex}
                        onSelectImage={setSelectedImageIndex}
                        showNavigationArrows
                        navigationArrowSize="lg"
                        badge={badge}
                        productName={productName}
                    />
                </div>
            </div>
            <ImageGalleryThumbs
                images={images}
                selectedImageIndex={selectedImageIndex}
                onSelectImage={setSelectedImageIndex}
                productName={productName}
            />
        </div>
    );
}
